"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import { Plus, Calendar, Users, TrendingUp, AlertCircle } from "lucide-react"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import MODAL_NAMES from "../../dialogs/ModalName"
import Modal from "../../../shared/components/Modal"
import AddRatingTerm from "./AddRatingTerm"

import showModalFromId from "../../../shared/functions/ModalShow"
import FeedbackTermTable from "./FeedbackTermTable"

const FeedbackReviewHome = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const assignments = store.ratingAssignments.all.map((assignment) => assignment.asJson)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        await Promise.all([api.user.getAll(), api.ratingAssignments.getAll()])
      } catch (error) {
        console.error("Error loading data:", error)
        setError("Failed to load feedback review data. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [api.user, api.ratingAssignments])

  const addRatingTerm = () => {
    showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_RATING_TERM)
  }

  // Calculate statistics
  const totalTerms = assignments.length
  const activeTerms = assignments.filter((a) => a.status?.toLowerCase() === "in progress").length
  const completedTerms = assignments.filter((a) => a.status?.toLowerCase() === "completed").length
  const completionRate = totalTerms > 0 ? Math.round((completedTerms / totalTerms) * 100) : 0

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-3xl font-bold mb-2">Feedback Review Terms</h1>
                  <p className="text-indigo-100">Manage and monitor your 360-degree feedback rating terms and cycles</p>
                </div>
                <button
                  onClick={addRatingTerm}
                  className="bg-white/20 border border-white/30 rounded-lg px-4 py-2 text-white hover:bg-white/30 transition-colors flex items-center space-x-2"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add Rating Term</span>
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Terms</p>
                  <p className="text-2xl font-bold text-gray-900">{totalTerms}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Users className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Terms</p>
                  <p className="text-2xl font-bold text-gray-900">{activeTerms}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{completedTerms}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Completion Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{completionRate}%</p>
                  <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                    <div
                      className="bg-purple-600 h-1 rounded-full transition-all duration-300"
                      style={{ width: `${completionRate}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feedback Terms Table */}
          <FeedbackTermTable data={assignments} />
        </div>

        {/* Modal */}
        <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_RATING_TERM}>
          <AddRatingTerm />
        </Modal>
      </div>
    </ErrorBoundary>
  )
})

export default FeedbackReviewHome
